import { createApi } from '@reduxjs/toolkit/query/react';
import { CacheTags, PaginatedRequest } from 'types';
import { PostAccountRequest } from './bindings/PostAccountRequest';
import { PostSubAccountRequest } from './bindings/PostSubAccountRequest';
import { PutAccountRequest } from './bindings/PutAccountRequest';
import { PutSubAccountRequest } from './bindings/PutSubAccountRequest';
import { AccountResponse } from './bindings/AccountResponse';
import { SubAccountResponse } from './bindings/SubAccountResponse';
import { HourSeconds, OdinBaseQuery } from './core';
import { FetchResponse } from './bindings/FetchResponse';

export const accountsApi = createApi({
  reducerPath: 'accounts',
  baseQuery: OdinBaseQuery,
  tagTypes: [CacheTags.accounts, CacheTags.subAccounts],
  endpoints: (builder) => ({
    fetchAccounts: builder.query<
      FetchResponse<AccountResponse>,
      PaginatedRequest
    >({
      keepUnusedDataFor: HourSeconds, // cache for an hour
      query: (payload) => ({
        url: '/api/accounts',
        method: 'GET',
        params: payload,
      }),
      providesTags: (res) => {
        const accounts =
          res?.items.map((x) => ({ type: CacheTags.accounts, id: x.id })) ?? [];
        const subAccounts =
          res?.items.flatMap((x) =>
            x.subAccounts.map((y) => ({
              type: CacheTags.subAccounts,
              id: y.subAccountId,
            }))
          ) ?? [];
        const partial = [{ type: CacheTags.accounts, id: CacheTags.partial }];
        const all = [...accounts, ...subAccounts, ...partial];
        return all;
      },
    }),
    postAccount: builder.mutation<AccountResponse, PostAccountRequest>({
      query: (payload) => ({
        url: '/api/accounts',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, __, req) => [
        { type: CacheTags.accounts, id: CacheTags.partial },
      ],
    }),
    postSubAccount: builder.mutation<SubAccountResponse, PostSubAccountRequest>(
      {
        query: (payload) => ({
          url: '/api/sub-accounts',
          method: 'POST',
          body: payload,
        }),
        invalidatesTags: [{ type: CacheTags.accounts, id: CacheTags.partial }],
      }
    ),
    putAccount: builder.mutation<
      AccountResponse,
      { id: string } & PutAccountRequest
    >({
      query: ({ id, ...payload }) => ({
        url: `/api/accounts/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, __, req) => [
        { type: CacheTags.accounts, id: CacheTags.partial },
        { type: CacheTags.accounts, id: req.id },
      ],
    }),
    putSubAccount: builder.mutation<
      SubAccountResponse,
      { id: string; accountId: string } & PutSubAccountRequest
    >({
      query: ({ id, ...payload }) => ({
        url: `/api/sub-accounts/${id}`,
        method: 'PUT',
        body: payload,
      }),
      // invalidatesTags: [CacheTags.accounts],
      invalidatesTags: (_, __, req) => [
        { type: CacheTags.accounts, id: CacheTags.partial },
        { type: CacheTags.accounts, id: req.accountId },
        { type: CacheTags.subAccounts, id: CacheTags.partial },
        { type: CacheTags.subAccounts, id: req.id },
      ],
    }),
  }),
});

export const {
  useFetchAccountsQuery,
  usePostAccountMutation,
  usePostSubAccountMutation,
  usePutAccountMutation,
  usePutSubAccountMutation,
} = accountsApi;
